/**
 * effectKnobSchema — declarative per-kind control descriptors that drive the
 * EffectLayerPanel's TYPED knob editor (Phase 3). Each EffectKind maps to a list
 * of controls (color picker / number / range slider / enum select / colour list);
 * the panel renders them and patches the layer field-by-field. Fields a kind has
 * but the schema omits are still editable via the raw-JSON box, so nothing is
 * ever locked out. (glowBlobs' nested `blobs` array is one such field — its
 * per-blob color/position/size/alpha are JSON-edited; the layer-level textureKey,
 * blendMode, and tintTo knobs cover the quick-tuning scalars.)
 *
 * Adding a kind's controls = add an entry below. Keep keys + ranges in sync with
 * the specs in config/atmosphere/effectLayer.types.ts.
 */
import type { EffectKind } from '../../../../config/atmosphere/effectLayer.types';

export type KnobType = 'color' | 'number' | 'range' | 'select' | 'colorList';

export interface KnobControl {
  key: string;
  type: KnobType;
  label?: string;
  min?: number;
  max?: number;
  step?: number;
  options?: string[]; // for 'select'
}

const A = { type: 'range', min: 0, max: 1, step: 0.02 } as const;      // alpha 0..1
const FRAC = { type: 'range', min: 0, max: 1, step: 0.02 } as const;   // 0..1 fraction
const BLEND = ['add', 'screen', 'normal'];

/** number helper */
const N = (key: string, step = 1): KnobControl => ({ key, type: 'number', step });

export const KIND_KNOBS: Partial<Record<EffectKind, KnobControl[]>> = {
  fireflies: [
    N('count'), { key: 'colors', type: 'colorList' },
    N('sizeMin'), N('sizeMax'), N('driftPx'),
    N('periodMinMs', 50), N('periodMaxMs', 50),
    { key: 'alphaMin', ...A }, { key: 'alphaMax', ...A },
  ],
  godRays: [
    N('count'), { key: 'color', type: 'color' },
    N('widthMin'), N('widthMax'), N('height'), N('tiltDeg'),
    N('periodMinMs', 50), N('periodMaxMs', 50),
    { key: 'alphaMin', ...A }, { key: 'alphaMax', ...A },
  ],
  weather: [
    { key: 'mode', type: 'select', options: ['fall', 'rise', 'swirl'] }, N('windX'),
    N('swirlRadiusMin'), N('swirlRadiusMax'), N('swirlTurns', 0.5),
  ],
  fog: [
    { key: 'color', type: 'color' }, N('bands'),
    { key: 'alphaMin', ...A }, { key: 'alphaMax', ...A },
    N('driftPx'), N('periodMinMs', 50), N('periodMaxMs', 50),
    { key: 'yMin', ...FRAC }, { key: 'yMax', ...FRAC }, N('sizeMin'), N('sizeMax'),
  ],
  flicker: [
    { key: 'color', type: 'color' }, N('size'),
    { key: 'baseAlpha', ...A }, { key: 'flickerAmp', ...A },
    N('hzMin', 0.5), N('hzMax', 0.5),
  ],
  candle: [
    { key: 'color', type: 'color' }, { key: 'haloColor', type: 'color' },
    { key: 'baseAlpha', ...A }, { key: 'flickerAmp', ...A },
    N('hzMin', 0.5), N('hzMax', 0.5),
    N('height'), N('width'), N('haloScale', 0.1), N('wobblePx', 0.5),
  ],
  vignette: [
    { key: 'color', type: 'color' }, { key: 'edgeAlpha', ...A },
    { key: 'washColor', type: 'color' }, { key: 'washAlpha', ...A },
  ],
  parallax: [
    { key: 'shape', type: 'select', options: ['cone', 'peaks', 'columns', 'dunes', 'spires', 'ridge', 'stalactites'] },
    { key: 'color', type: 'color' }, N('speed', 0.05),
    { key: 'plane', type: 'select', options: ['background', 'foreground', 'ceiling'] },
    N('y'), N('height'), { key: 'alpha', ...A }, N('detail', 0.1),
  ],
  skyGradient: [
    { key: 'from', type: 'color' }, { key: 'to', type: 'color' }, N('bands'),
  ],
  celestial: [
    { key: 'atX', ...FRAC }, { key: 'atY', ...FRAC },
    { key: 'color', type: 'color' }, { key: 'haloColor', type: 'color' },
    N('size'), N('haloScale', 0.1), { key: 'coreAlpha', ...A }, { key: 'haloAlpha', ...A },
  ],
  critters: [
    N('count'), { key: 'palette', type: 'colorList' },
    N('sizeMin'), N('sizeMax'), N('speedMin'), N('speedMax'),
    { key: 'bandYMin', ...FRAC }, { key: 'bandYMax', ...FRAC },
    { key: 'direction', type: 'select', options: ['ltr', 'rtl', 'both'] },
  ],
  cloudBands: [
    N('count'), { key: 'color', type: 'color' },
    N('sizeMin'), N('sizeMax'), { key: 'alphaMin', ...A }, { key: 'alphaMax', ...A },
    N('driftPxPerSec'), { key: 'bandYMin', ...FRAC }, { key: 'bandYMax', ...FRAC },
  ],
  colorGrade: [
    { key: 'top', type: 'color' }, { key: 'bottom', type: 'color' },
    { key: 'strength', type: 'range', min: 0, max: 0.5, step: 0.01 }, N('bands'),
    { key: 'blend', type: 'select', options: BLEND },
  ],
  aurora: [
    { key: 'colors', type: 'colorList' }, N('bands'),
    N('widthMin'), N('widthMax'), N('heightMin'), N('heightMax'),
    { key: 'alphaMin', ...A }, { key: 'alphaMax', ...A },
    { key: 'bandYMin', ...FRAC }, { key: 'bandYMax', ...FRAC },
    N('swayPx'), N('periodMinMs', 50), N('periodMaxMs', 50), N('driftPxPerSec'),
  ],
  lightning: [
    { key: 'color', type: 'color' }, N('bolts'),
    N('intervalMinMs', 100), N('intervalMaxMs', 100),
    { key: 'flashAlpha', ...A }, { key: 'boltAlpha', ...A },
  ],
  shootingStar: [
    { key: 'color', type: 'color' }, N('count'),
    N('intervalMinMs', 100), N('intervalMaxMs', 100),
    N('lengthMin'), N('lengthMax'), N('thickness'), N('speed'), N('angleDeg'),
    { key: 'alphaMax', ...A },
  ],
  caustics: [
    { key: 'color', type: 'color' }, N('count'),
    N('sizeMin'), N('sizeMax'), { key: 'alphaMin', ...A }, { key: 'alphaMax', ...A },
    { key: 'bandYMin', ...FRAC }, { key: 'bandYMax', ...FRAC },
    N('driftPx'), N('periodMinMs', 50), N('periodMaxMs', 50),
  ],
  bioluminescence: [
    { key: 'color', type: 'color' }, N('count'),
    N('sizeMin'), N('sizeMax'), { key: 'alphaMin', ...A }, { key: 'alphaMax', ...A },
    { key: 'bandYMin', ...FRAC }, { key: 'bandYMax', ...FRAC },
    N('driftPx'), N('periodMinMs', 50), N('periodMaxMs', 50),
  ],
  heatHaze: [
    { key: 'color', type: 'color' }, N('count'),
    N('sizeMin'), N('sizeMax'), { key: 'alphaMin', ...A }, { key: 'alphaMax', ...A },
    { key: 'bandYMin', ...FRAC }, { key: 'bandYMax', ...FRAC },
    N('driftPx'), N('periodMinMs', 50), N('periodMaxMs', 50),
  ],
  glowBlobs: [
    // `blobs` is a nested array (color+atX+atY+size+alpha per blob) — edited via
    // the raw-JSON box. These layer-level knobs are the quick-tuning scalars.
    { key: 'textureKey', type: 'select', options: ['glow_soft', 'sparkle', 'glow_sharp'] },
    { key: 'blendMode', type: 'select', options: ['add', 'screen', 'normal'] },
    { key: 'tintTo', type: 'color' },
  ],
  lightField: [
    N('count'), { key: 'palette', type: 'colorList' },
    N('sizeMin'), N('sizeMax'),
    { key: 'alphaMin', ...A }, { key: 'alphaMax', ...A },
    N('driftRadius'), N('periodMinMs', 50), N('periodMaxMs', 50),
  ],
  bloom: [
    N('x'), N('y'), N('size'),
    { key: 'color', type: 'color' },
    { key: 'alphaMin', ...A }, { key: 'alphaMax', ...A },
    { key: 'scaleMin', type: 'range', min: 0.5, max: 2, step: 0.05 },
    { key: 'scaleMax', type: 'range', min: 0.5, max: 2, step: 0.05 },
    N('periodMs', 50),
  ],
  leviathans: [
    { key: 'shape', type: 'select', options: ['whaleSkeleton'] },
    { key: 'edgeHighlight', type: 'select', options: ['true', 'false'] },
    { key: 'edgeColor', type: 'color' },
    N('detail', 0.1),
    { key: 'direction', type: 'select', options: ['left', 'right', 'both'] },
    { key: 'bandYMin', ...FRAC }, { key: 'bandYMax', ...FRAC },
    N('bob.ampPx'), N('bob.periodMinMs', 500), N('bob.periodMaxMs', 500),
  ],
  schoolingMotes: [
    N('count'), N('schools'), { key: 'palette', type: 'colorList' },
    N('sizeMin'), N('sizeMax'), N('speed'),
    { key: 'cohesion', ...FRAC }, { key: 'alignment', ...FRAC }, { key: 'separation', ...FRAC },
    { key: 'bandYMin', ...FRAC }, { key: 'bandYMax', ...FRAC },
    { key: 'startle', type: 'select', options: ['true', 'false'] },
  ],
};

// --- colour <-> hex helpers (shared by the typed form) ----------------------

/** 0xRRGGBB number → '#rrggbb' (for <input type="color">). */
export function numToHex(n: number | undefined): string {
  const v = typeof n === 'number' && n >= 0 ? n : 0;
  return `#${(v & 0xffffff).toString(16).padStart(6, '0')}`;
}

/** '#rrggbb' → 0xRRGGBB number. */
export function hexToNum(hex: string): number {
  return parseInt(hex.replace('#', ''), 16) || 0;
}

/** number[] → "#rrggbb, #rrggbb" editable string. */
export function colorListToText(arr: unknown): string {
  if (!Array.isArray(arr)) return '';
  return arr.map((n) => numToHex(typeof n === 'number' ? n : 0)).join(', ');
}

/** "#rrggbb, 0x..., ..." → number[] (drops empties; tolerates bare hex). */
export function textToColorList(text: string): number[] {
  return text.split(',').map((s) => s.trim()).filter(Boolean).map((s) => {
    if (s.startsWith('#')) return hexToNum(s);
    if (s.startsWith('0x')) return parseInt(s, 16) || 0;
    return parseInt(s, 16) || 0;
  });
}
